import { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { LoadingContext } from "../../../../../contexts/Loading/LoadingContext";
import { DashboardContext } from "../../../../../contexts/Dashboard/DashboardContext";
import { api, errorTreatment } from "../../../../../services/api";
import { ChartBar } from "./chart";
import "../../style.css";

export interface Documents {
  data: {
    documents: { [propriedade: string]: string }[];
  };
}

const GROUP_BY_STORAGE_KEY = "chartAdoptionRateRegion_groupBy";

type GroupByOption = "documents" | "stops";

const getStoredGroupBy = (screenId: string | undefined): GroupByOption => {
  try {
    const key = screenId ? `${GROUP_BY_STORAGE_KEY}_${screenId}` : GROUP_BY_STORAGE_KEY;
    const stored = localStorage.getItem(key);
    if (stored === "documents" || stored === "stops") return stored;
  } catch {
    // ignore
  }
  return "documents";
};

const setStoredGroupBy = (value: GroupByOption, screenId: string | undefined) => {
  try {
    const key = screenId ? `${GROUP_BY_STORAGE_KEY}_${screenId}` : GROUP_BY_STORAGE_KEY;
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
};

export const ChartAdoptionRateRegion = () => {
  const { changeVisibility } = useContext(LoadingContext);
  const { reloadComponent } = useContext(DashboardContext);
  const { shortToken, id, mode } = useParams<{
    shortToken?: string;
    id?: string;
    mode?: string;
  }>();

  const [documents, setDocuments] = useState<Documents["data"]>({ documents: [] });
  const [completeRequisition, setCompleteRequisition] = useState(false);
  const [groupBy, setGroupBy] = useState<GroupByOption>(() => getStoredGroupBy(id));

  const baseRoute =
    shortToken
      ? `/adoptionRate?mode=regions&short_token=${shortToken}&screen_id=${id}&group_by=${groupBy}`
      : `/adoptionRate?mode=regions&screen_id=${id}&group_by=${groupBy}`;

  const requisition = useCallback(async () => {
    return api
      .get(baseRoute)
      .then((response: Documents) => {
        setDocuments(response.data);
      })
      .catch((error) => {
        errorTreatment(error);
      });
  }, [baseRoute]);

  useEffect(() => {
    setGroupBy((prev) => getStoredGroupBy(id));
  }, [id]);

  useEffect(() => {
    changeVisibility(true);
    const fetchData = async () => {
      try {
        await requisition();
      } finally {
        changeVisibility(false);
        setCompleteRequisition(true);
      }
    };
    fetchData();

    if (mode === "view") {
      const intervalId = setInterval(requisition, 60000);
      return () => clearInterval(intervalId);
    }
  }, [requisition, mode]);

  const handleGroupByChange = (next: GroupByOption) => {
    setGroupBy(next);
    setStoredGroupBy(next, id);
  };

  if (!completeRequisition || reloadComponent) {
    return <></>;
  }

  return (
    <div id="chart-adoption-rate-region" className="h100">
      <ChartBar
        data={documents}
        groupBy={groupBy}
        mode={mode}
        onGroupByChange={handleGroupByChange}
      />
    </div>
  );
};
